import CommonModal from "@/components/common/modal";
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
    Table,
    Button,
    Checkbox,
    Select,
    Space,
    Empty,
    Switch,
    Input,
    Modal,
    Form,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Api } from "@/api/api";
import { showConfirm, showDanger, showSuccess, showWarning } from "@/utils";
import { useLoading } from "@/components/global-loading";
import styles from "./DeviceModal.css";
import { FormattedMessage, injectIntl } from "react-intl";
const { Option } = Select;

const DeviceModal = ({ agentInfo, visible, onClose, intl }) => {
    const { showLoading, hideLoading } = useLoading();
    const firmwareTypes = useRef([]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [dataSource, setDataSource] = useState(null);
    const [editingKey, setEditingKey] = useState("");
    const [editingRemark, setEditingRemark] = useState("");

    const [isManualAddModalVisible, setIsManualAddModalVisible] =
        useState(false);

    const [isVerificationBindModalVisible, setIsVerificationBindModalVisible] =
        useState(false);

    const handleEdit = (record) => {
        setEditingKey(record.device_id);
        setEditingRemark(record.remark || "");
    };

    const handleSave = (record) => {
        try {
            showLoading(intl.formatMessage({ id: "gui.agentManage.saving", defaultMessage: "Saving..." }));
            updateDeviceInfo(
                record.device_id,
                { alias: editingRemark },
                (result, { msg }) => {
                    hideLoading();
                    if (result) {
                        showSuccess(intl.formatMessage({ id: "gui.agentManage.remarkUpdateSuccess", defaultMessage: "Remark updated successfully" }));
                        setDataSource((prevDataSource) =>
                            prevDataSource.map((item) =>
                                item.device_id === record.device_id
                                    ? { ...item, remark: editingRemark }
                                    : item
                            )
                        );
                        setEditingKey("");
                        setEditingRemark("");
                    } else {
                        showDanger(msg || intl.formatMessage({ id: "gui.agentManage.remarkUpdateFailed", defaultMessage: "Failed to update remark" }));
                    }
                }
            );
        } catch (error) {
            hideLoading();
            showDanger(intl.formatMessage({ id: "gui.agentManage.remarkUpdateFailed", defaultMessage: "Failed to update remark" }));
            console.error("Failed to save remark:", error);
        }
    };

    // 表格列配置 - 使用 useMemo 确保语言切换时更新
    const columns = React.useMemo(() => [
        {
            title: intl.formatMessage({ id: "gui.agentManage.remark", defaultMessage: "Remark" }),
            align: "center",
            dataIndex: "remark",
            key: "remark",
            width: 120,
            render: (text, record) => {
                const isEditing = record.device_id === editingKey;
                console.log(text, record);
                if(record.source == 1){
                    return isEditing ? (
                        <Input
                            value={editingRemark}
                            onChange={(e) => setEditingRemark(e.target.value)}
                            onBlur={() => handleSave(record)}
                            onPressEnter={() => handleSave(record)}
                            autoFocus
                        />
                    ) : (
                        <div flex="cross:center main:center">
                            <EditOutlined
                                style={{
                                    marginRight: 6,
                                    cursor: "pointer",
                                    color: "#1890ff",
                                }}
                                onClick={() => handleEdit(record)}
                            />
                            <span>{text}</span>
                        </div>
                    );
                } else {
                    return (<span>{text}</span>);
                }

            },
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.deviceModel", defaultMessage: "Device Model" }),
            align: "center",
            dataIndex: "deviceModel",
            key: "deviceModel",
            width: 120,
            render: (text, record) => getFirmwareTypeName(record.model),
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.firmwareVersion", defaultMessage: "Firmware Version" }),
            align: "center",
            dataIndex: "firmwareVersion",
            key: "firmwareVersion",
            width: 120,
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.macAddress", defaultMessage: "Mac Address" }),
            align: "center",
            dataIndex: "macAddress",
            key: "macAddress",
            width: 150,
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.bindTime", defaultMessage: "Bind Time" }),
            align: "center",
            dataIndex: "bindTime",
            key: "bindTime",
            width: 150,
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.lastConversation", defaultMessage: "Last Conversation" }),
            align: "center",
            dataIndex: "lastConversation",
            key: "lastConversation",
            width: 150,
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.otaUpgrade", defaultMessage: "OTA Upgrade" }),
            align: "center",
            dataIndex: "otaUpgrade",
            key: "otaUpgrade",
            width: 100,
            render: (text, record) => (
                <Switch
                    onChange={(checked) =>
                        handleOtaSwitchChange(record, checked)
                    }
                    checked={record.otaSwitch}
                />
            ),
        },
        {
            title: intl.formatMessage({ id: "gui.agentManage.operation", defaultMessage: "Operation" }),
            align: "center",
            dataIndex: "operation",
            key: "operation",
            width: 80,
            render: (_, record) => {
                return record.source == 1 ? (
                    <Button
                        danger
                        size="small"
                        onClick={() => handleDelete(record)}
                    >
                        <FormattedMessage id="gui.agentManage.unbind" defaultMessage="Unbind" />
                    </Button>
                ) : (
                    <span><FormattedMessage id="gui.agentManage.teacherBind" defaultMessage="Teacher Bind" /></span>
                )
            },
        },
    ], [intl, editingKey, editingRemark]);

    // 处理行选择
    const handleSelectRow = useCallback((key, checked) => {
        if (checked) {
            setSelectedRowKeys((prev) => [...prev, key]);
        } else {
            setSelectedRowKeys((prev) => prev.filter((k) => k !== key));
        }
    }, []);

    const getFirmwareTypes = async () => {
        try {
            const res = await Api.dict.getDictDataByType("FIRMWARE_TYPE");
            firmwareTypes.current = res.data;
        } catch (error) {
            console.error(intl.formatMessage({ id: "gui.agentManage.getFirmwareTypeError", defaultMessage: "Failed to get firmware type:" }), error);
            showDanger(error.message || intl.formatMessage({ id: "gui.agentManage.getFirmwareTypeFailed", defaultMessage: "Failed to get firmware type" }));
        }
    };

    // 处理全选
    const handleSelectAll = useCallback(() => {
        if (selectedRowKeys.length === dataSource.length) {
            setSelectedRowKeys([]);
        } else {
            setSelectedRowKeys(dataSource.map((item) => item.device_id));
        }
    }, [selectedRowKeys.length, dataSource]);

    // 处理验证码绑定
    const handleVerificationBind = useCallback(() => {
        setIsVerificationBindModalVisible(true);
    }, []);
    // 处理手动添加
    const handleManualAdd = useCallback(() => {
        setIsManualAddModalVisible(true);
    }, []);
    const batchUnbindDevices = (deviceIds) => {
        showLoading(intl.formatMessage({ id: "gui.agentManage.unbinding", defaultMessage: "Unbinding..." }));
        const promises = deviceIds.map((id) => {
            return new Promise((resolve, reject) => {
                Api.device.unbindDevice(id, ({ data }) => {
                    if (data.code === 0) {
                        resolve();
                    } else {
                        reject(data.msg || intl.formatMessage({ id: "gui.agentManage.unbindFailed", defaultMessage: "Unbind failed" }));
                    }
                });
            });
        });
        Promise.all(promises)
            .then(() => {
                hideLoading();
                showSuccess(intl.formatMessage({ id: "gui.agentManage.batchUnbindSuccess", defaultMessage: "Successfully unbound {count} devices" }, { count: deviceIds.length }));
                fetchBindDevices();
                setSelectedRowKeys([]);
            })
            .catch((error) => {
                hideLoading();
                showDanger(error || intl.formatMessage({ id: "gui.agentManage.batchUnbindError", defaultMessage: "Error occurred during batch unbind" }));
            });
    };
    // 处理批量解绑
    const handleUnbind = async () => {
        if (selectedRowKeys.length === 0) {
            showWarning(intl.formatMessage({ id: "gui.agentManage.selectDeviceToUnbind", defaultMessage: "Please select devices to unbind" }));
            return;
        }

        try {
            const resp = await showConfirm(
                intl.formatMessage({ id: "gui.agentManage.confirmBatchUnbind", defaultMessage: "Are you sure you want to unbind the selected {count} devices?" }, { count: selectedRowKeys.length })
            );
            if (resp) {
                batchUnbindDevices(selectedRowKeys);
            }
        } catch (error) {
            showDanger(intl.formatMessage({ id: "gui.agentManage.unbindFailed", defaultMessage: "Unbind failed" }));
        }
    };

    // 处理单个解绑
    const handleDelete = async (record) => {
        console.log(record);
        let device_id = record.device_id
        let typeLabel = getFirmwareTypeName(record.model)
        let str = record.remark? `${record.remark} - ${typeLabel}` : typeLabel
        try {
            const resp = await showConfirm(intl.formatMessage({ id: "gui.agentManage.confirmUnbindDevice", defaultMessage: "Are you sure you want to unbind device \"{name}\"?" }, { name: str }));
            if (resp) {
                showLoading(intl.formatMessage({ id: "gui.agentManage.unbinding", defaultMessage: "Unbinding..." }));
                Api.device.unbindDevice(device_id, ({ data }) => {
                    hideLoading();
                    if (data.code === 0) {
                        showSuccess(intl.formatMessage({ id: "gui.agentManage.deviceUnbindSuccess", defaultMessage: "Device unbound successfully" }));
                        fetchBindDevices();
                    } else {
                        showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.deviceUnbindFailed", defaultMessage: "Failed to unbind device" }));
                    }
                });
            }
        } catch (error) {
            hideLoading();
        }
    };

    const initData = async () => {
        await getFirmwareTypes();
        fetchBindDevices();
    };

    useEffect(() => {
        if (agentInfo?.id) {
            initData();
        }
    }, [agentInfo?.id]);

    const getFirmwareTypeName = (type) => {
        const firmwareType = firmwareTypes.current.find(
            (item) => item.key === type
        );
        return firmwareType ? firmwareType.name : type;
    };

    const fetchBindDevices = () => {
        showLoading();
        Api.device.getAgentBindDevices(agentInfo?.id, ({ data }) => {
            hideLoading();
            // this.loading = false;
            if (data.code === 0) {
                const newData = data.data
                    .map((device) => {
                        return {
                            device_id: device.id,
                            model: device.board,
                            firmwareVersion: device.appVersion,
                            macAddress: device.macAddress,
                            bindTime: device.createDate,
                            lastConversation: device.lastConnectedAt,
                            remark: device.alias,
                            _originalRemark: device.alias,
                            isEdit: false,
                            _submitting: false,
                            otaSwitch: device.autoUpdate === 1,
                            rawBindTime: new Date(device.createDate).getTime(),
                            source: device.source,
                        };
                    })
                    .sort((a, b) => a.rawBindTime - b.rawBindTime);

                setDataSource(newData);
                // this.activeSearchKeyword = "";
                // this.searchKeyword = "";
            } else {
                showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.getDeviceListFailed", defaultMessage: "Failed to get device list" }));
            }
        });
    };

    const updateDeviceInfo = (device_id, payload, callback) => {
        return Api.device.updateDeviceInfo(device_id, payload, ({ data }) => {
            callback(data.code === 0, data);
        });
    };

    const handleOtaSwitchChange = (row, newStates) => {
        showLoading(intl.formatMessage({ id: "gui.agentManage.updating", defaultMessage: "Updating..." }));
        updateDeviceInfo(
            row.device_id,
            { autoUpdate: newStates ? 1 : 0 },
            (result, { msg }) => {
                hideLoading();
                if (result) {
                    showSuccess(
                        newStates ? intl.formatMessage({ id: "gui.agentManage.autoUpdateEnabled", defaultMessage: "Auto update enabled" }) : intl.formatMessage({ id: "gui.agentManage.autoUpdateDisabled", defaultMessage: "Auto update disabled" })
                    );
                    setDataSource((prevDataSource) => {
                        const newDataSource = prevDataSource.map((item) => {
                            if (item.device_id === row.device_id) {
                                item.otaSwitch = newStates;
                            }
                            return item;
                        });

                        return newDataSource;
                    });
                    return;
                }
                showDanger(msg || intl.formatMessage({ id: "gui.agentManage.operationFailed", defaultMessage: "Operation failed" }));
            }
        );
    };

    const handleManualAddSubmit = (values) => {
        const params = {
            agentId: agentInfo?.id,
            ...values,
        };
        showLoading(intl.formatMessage({ id: "gui.agentManage.adding", defaultMessage: "Adding..." }));
        Api.device.manualAddDevice(params, ({ data }) => {
            hideLoading();
            if (data.code === 0) {
                showSuccess(intl.formatMessage({ id: "gui.agentManage.deviceAddSuccess", defaultMessage: "Device added successfully" }));
                setIsManualAddModalVisible(false);
                fetchBindDevices(); // Refresh the device list
            } else {
                showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.addFailed", defaultMessage: "Add failed" }));
            }
        });
    };

    const handleManualBindSubmit = (values) => {
        if (!/^\d{6}$/.test(values.deviceCode)) {
            showDanger(intl.formatMessage({ id: "gui.agentManage.enter6DigitCode", defaultMessage: "Please enter 6-digit verification code" }));
            return;
        }
        showLoading(intl.formatMessage({ id: "gui.agentManage.adding", defaultMessage: "Adding..." }));
        Api.device.bindDevice(agentInfo.id, values.deviceCode, ({ data }) => {
            hideLoading();
            if (data.code === 0) {
                showSuccess(intl.formatMessage({ id: "gui.agentManage.deviceBindSuccess", defaultMessage: "Device bound successfully" }));
                setIsVerificationBindModalVisible(false);
                fetchBindDevices();
            } else {
                showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.bindFailed", defaultMessage: "Bind failed" }));
            }
        });
    };

    return (
        <>
            <CommonModal
                visible={visible}
                onClose={onClose}
                title={`${agentInfo?.name || "-"}-${intl.formatMessage({ id: "gui.agentManage.agent", defaultMessage: "Agent" })}`} // Title for the common modal
            >
                <div className={styles.container}>
                    <div style={{ paddingBottom: 36, justifyContent: 'space-between' }} flex="items-center">
                        <div flex="items-center">
                            <div style={{ marginRight: 8 }}>
                                <img
                                    src={require("@/assets/robot.png")}
                                    style={{ width: 25, height: "auto" }}
                                />
                            </div>
                            <div style={{ fontSize: 20, color: "#666" }}>
                                <FormattedMessage id="gui.agentManage.deviceManagement" defaultMessage="Device Management" />
                            </div>
                        </div>
                        <Tips intl={intl} />
                    </div>
                    {/* 表格 */}
                    <div
                        className={styles.tableContainer}
                        style={{ position: "relative" }}
                    >
                        {dataSource?.length > 0 ? (
                            <Table
                                rowKey={"device_id"}
                                columns={columns}
                                dataSource={dataSource}
                                pagination={false}
                                size="middle"
                            />
                        ) : (
                            <Empty description={intl.formatMessage({ id: "gui.agentManage.noData", defaultMessage: "No data" })} />
                        )}
                    </div>
                    {/* 操作按钮区 */}
                    <div className={styles.actionBar}>
                        <Space size="middle">
                            {/*<Button*/}
                            {/*    type="primary"*/}
                            {/*    style={{*/}
                            {/*        backgroundColor: "#855CD6",*/}
                            {/*        width: 120,*/}
                            {/*    }}*/}
                            {/*    className={styles.selectAllBtn}*/}
                            {/*    onClick={handleSelectAll}*/}
                            {/*>*/}
                            {/*    全选*/}
                            {/*</Button>*/}
                            <Button
                                type="primary"
                                style={{
                                    backgroundColor: "#5BC98C",
                                    width: 120,
                                }}
                                className={styles.verifyBtn}
                                onClick={handleVerificationBind}
                            >
                                <FormattedMessage id="gui.agentManage.verificationBind" defaultMessage="Verification Bind" />
                            </Button>
                            <Button
                                type="primary"
                                style={{
                                    backgroundColor: "#5BC98C",
                                    width: 120,
                                }}
                                className={styles.addBtn}
                                onClick={handleManualAdd}
                            >
                                <FormattedMessage id="gui.agentManage.manualAdd" defaultMessage="Manual Add" />
                            </Button>
                            {/*<Button*/}
                            {/*    type="primary"*/}
                            {/*    style={{*/}
                            {/*        backgroundColor: "#FD5B63",*/}
                            {/*        width: 120,*/}
                            {/*    }}*/}
                            {/*    icon={<DeleteOutlined />}*/}
                            {/*    className={styles.unbindBtn}*/}
                            {/*    onClick={handleUnbind}*/}
                            {/*>*/}
                            {/*    解绑*/}
                            {/*</Button>*/}
                        </Space>
                        {/* <div>
                        <Pagination defaultCurrent={1} total={50} />
                    </div> */}
                    </div>
                </div>
            </CommonModal>
            <ManualAddDeviceModal
                visible={isManualAddModalVisible}
                firmwareTypes={firmwareTypes.current}
                onCancel={() => setIsManualAddModalVisible(false)}
                onFinish={handleManualAddSubmit}
                intl={intl}
            />
            <VerificationBindModal
                visible={isVerificationBindModalVisible}
                onCancel={() => setIsVerificationBindModalVisible(false)}
                onFinish={handleManualBindSubmit}
                intl={intl}
            />
        </>
    );
};
const Tips = ({ intl }) => {
    const userRole = localStorage.getItem('roles');
    const isTeacher = userRole === intl.formatMessage({ id: "gui.agentManage.teacherRole", defaultMessage: "授课老师" });
    
    if (isTeacher) {
        return (<span style={{ fontSize: 16, color: "#FF0000", lineHeight: '32px' }}><FormattedMessage id="gui.agentManage.teacherNote1" defaultMessage="Note: Teachers need to view and unbind devices already bound to students in the class" /></span>)
    } else {
        return (<span style={{ fontSize: 16, color: "#FF0000", lineHeight: '32px' }}><FormattedMessage id="gui.agentManage.teacherNote2" defaultMessage="Note: Devices assigned by teachers cannot be unbound or edited" /></span>)
    }
}
const ManualAddDeviceModal = ({
    visible,
    onCancel,
    onFinish,
    firmwareTypes,
    intl,
}) => {
    const [form] = Form.useForm();

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                form.resetFields();
                onFinish(values);
            })
            .catch((info) => {
                console.log("Validate Failed:", info);
            });
    };

    return (
        <Modal
            title={<FormattedMessage id="gui.agentManage.manualAddDevice" defaultMessage="Manual Add Device" />}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            destroyOnHidden={true}
        >
            <Form form={form} layout="vertical" name="manual_add_device_form">
                <Form.Item
                    name="board"
                    label={intl.formatMessage({ id: "gui.agentManage.deviceModel", defaultMessage: "Device Model" })}
                    rules={[{ required: true, message: intl.formatMessage({ id: "gui.agentManage.pleaseSelectDeviceModel", defaultMessage: "Please select device model!" }) }]}
                >
                    <Select placeholder={intl.formatMessage({ id: "gui.agentManage.selectDeviceModel", defaultMessage: "Please select device model" })}>
                        {(firmwareTypes || []).map((item) => (
                            <Option key={item.key} value={item.key}>
                                {item.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="appVersion"
                    label={intl.formatMessage({ id: "gui.agentManage.firmwareVersion", defaultMessage: "Firmware Version" })}
                    rules={[{ required: true, message: intl.formatMessage({ id: "gui.agentManage.pleaseEnterFirmwareVersion", defaultMessage: "Please enter firmware version!" }) }]}
                >
                    <Input placeholder={intl.formatMessage({ id: "gui.agentManage.enterFirmwareVersion", defaultMessage: "Please enter firmware version" })} />
                </Form.Item>
                <Form.Item
                    name="macAddress"
                    label={intl.formatMessage({ id: "gui.agentManage.macAddress", defaultMessage: "Mac Address" })}
                    rules={[
                        { required: true, message: intl.formatMessage({ id: "gui.agentManage.pleaseEnterMacAddress", defaultMessage: "Please enter Mac address!" }) },
                        {
                            validator: async (_, value) => {
                                const macRegex =
                                    /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
                                if (!value) {
                                    return Promise.reject(
                                        new Error(intl.formatMessage({ id: "gui.agentManage.macAddressRequired", defaultMessage: "Please enter Mac address" }))
                                    );
                                }
                                if (!macRegex.test(value)) {
                                    return Promise.reject(
                                        new Error(
                                            intl.formatMessage({ id: "gui.agentManage.macAddressFormat", defaultMessage: "Please enter correct Mac address format, e.g.: 00:1A:2B:3C:4D:5E" })
                                        )
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input placeholder={intl.formatMessage({ id: "gui.agentManage.enterMacAddress", defaultMessage: "Please enter Mac address" })} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

const VerificationBindModal = ({ visible, onCancel, onFinish, intl }) => {
    const [form] = Form.useForm();

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                form.resetFields();
                onFinish(values);
            })
            .catch((info) => {
                console.log("Validate Failed:", info);
            });
    };

    return (
        <Modal
            title={<FormattedMessage id="gui.agentManage.addDevice" defaultMessage="Add Device" />}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            destroyOnHidden={true}
        >
            <Form form={form} layout="vertical" name="verification_bind_form">
                <Form.Item
                    name="deviceCode"
                    label={intl.formatMessage({ id: "gui.agentManage.verificationCode", defaultMessage: "Verification Code" })}
                    rules={[{ required: true, message: intl.formatMessage({ id: "gui.agentManage.enterVerificationCode", defaultMessage: "Please enter verification code!" }) }]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: "gui.agentManage.enter6DigitCode", defaultMessage: "Please enter the 6-digit verification code broadcasted by the device" })}
                        maxLength={6}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default injectIntl(DeviceModal);